/* eslint-disable no-bad-map-access/no-bad-map-access */
/// <reference types="@arcgis/map-components/types/react" />
import '@arcgis/map-components/dist/components/arcgis-map'
import '@arcgis/map-components/components/arcgis-zoom'

import { memo, useCallback, useEffect, useRef, useState } from 'react'

import 'leaflet/dist/leaflet.css'
import esriConfig from '@arcgis/core/config.js'
import { on, watch } from '@arcgis/core/core/reactiveUtils'
import Multipoint from '@arcgis/core/geometry/Multipoint'
import Polygon from '@arcgis/core/geometry/Polygon'
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils'
import Graphic from '@arcgis/core/Graphic'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol'
import MapView from '@arcgis/core/views/MapView.js'
import Locate from '@arcgis/core/widgets/Locate.js'
import { TargetedEvent } from '@arcgis/map-components'
import SegmentedControl from 'govwell-ui/components/SegmentedControl/SegmentedControl'
import { twMerge } from 'tailwind-merge'
import { useLocalStorage } from 'usehooks-ts'

import { EnvVariables } from 'src/constants'
import { useCurrentBreakpoint } from 'src/hooks/use-current-breakpoint'
import { LocalStorageKey } from 'src/types'
import { filterNil } from 'src/utils'

import { getGraphicShapeKey } from './graphicShapeKey'
import {
  getInteractiveGraphicFromShapeGraphics,
  getPopupFeatureGraphicForShapeKey,
} from './popupFeatureGraphic'
import { MapProps, MapShape } from './types'
import {
  getCentroidPoint,
  getUnselectedSymbol,
  getSelectedSymbol,
  getHighlightedPolygonFillSymbol,
  getPolygonFillSymbol,
  getTransparentFillSymbol,
} from './util'

import './Map.css'

export const MapHeightPx = 291
const zoom = 16
enum Basemap {
  Map = 'arcgis/community',
  Satellite = 'arcgis/imagery',
}

esriConfig.apiKey = EnvVariables.ArcGISApiKey
esriConfig.apiKeys = {
  basemapStyles: EnvVariables.ArcGISApiKey,
}

const getGraphicsFromHitTest = (response: __esri.HitTestResult) =>
  filterNil(response.results.map((r) => (r.type === 'graphic' ? r.graphic : null))).filter(
    (g) => !!g.layer?.type
  )

const ArcGisMap = (props: MapProps) => {
  const {
    isLoading,
    onMapBoundariesChange,
    onShapesRendered,
    isUserLocationEnabled,
    shouldZoomWhenShapesUpdated = true,
  } = props
  const [shapes, setShapes] = useState(props.shapes)
  const [basemap, setBasemap] = useLocalStorage<Basemap>(
    LocalStorageKey.ArcgisBasemapKey,
    Basemap.Map
  )

  useEffect(() => {
    if (isLoading) {
      return // Show the previously loaded map until the new query is complete, otherwise it's a long period of showing a blank map
    }
    setShapes(props.shapes)
  }, [isLoading, props.shapes])

  const [view, setView] = useState<MapView>()
  const handleViewReady = useCallback((event: TargetedEvent<HTMLArcgisMapElement, void>) => {
    event.target.view.popupEnabled = false
    setView(event.target.view)
  }, [])

  const { isSmallerThanOrEqual } = useCurrentBreakpoint()

  const isVerySmallScreen = isSmallerThanOrEqual('xs')

  // Cache of graphics per shapeKey
  const shapeKeyToGraphicsRef = useRef<Map<string, Graphic[]>>(new Map<string, Graphic[]>())

  // Cache of shapes by shapeKey for action dispatch
  const shapeKeyToShapeRef = useRef<Map<string, MapShape>>(new Map<string, MapShape>())

  // The currently selected shape key (user has clicked on a point)
  const selectedShapeKeyRef = useRef<string | null>(null)

  // The currently hovered shape keys
  const hoveredShapeKeysRef = useRef<Set<string>>(new Set())

  // Whether the first render with special zoom behavior is complete
  const isFirstRenderCompletedRef = useRef(false)

  const [isPopupVisible, setIsPopupVisible] = useState(false)

  const isTransparentPolygonGraphic = useCallback((graphic: Graphic) => {
    if (graphic.geometry?.type !== 'polygon') {
      return false
    }

    const fillSymbol = graphic.symbol
    if (!(fillSymbol instanceof SimpleFillSymbol)) {
      return false
    }

    const color = fillSymbol.color
    return Array.isArray(color) && color[3] === 0
  }, [])

  const applyShapeKeyHighlightState = useCallback(
    (shapeKey: string, isHighlighted: boolean) => {
      const shape = shapeKeyToShapeRef.current.get(shapeKey)
      const graphics = shapeKeyToGraphicsRef.current.get(shapeKey) ?? []
      const isPopupSelected = selectedShapeKeyRef.current === shapeKey

      for (const graphic of graphics) {
        if (graphic.geometry?.type === 'point') {
          const isShapeSelected = shape?.isSelected ?? false
          if (isShapeSelected || isPopupSelected) {
            graphic.symbol = getSelectedSymbol()
          } else {
            graphic.symbol = isHighlighted ? getSelectedSymbol() : getUnselectedSymbol()
          }
          continue
        }

        if (graphic.geometry?.type === 'polygon' && shape?.showPin === false) {
          if (isTransparentPolygonGraphic(graphic)) {
            continue
          }

          const baseSymbol = shape.symbol ?? getPolygonFillSymbol()
          graphic.symbol =
            isHighlighted || isPopupSelected ? getHighlightedPolygonFillSymbol() : baseSymbol
        }
      }
    },
    [isTransparentPolygonGraphic, shapeKeyToGraphicsRef, shapeKeyToShapeRef]
  )

  const bringInteractiveGraphicToFront = useCallback(
    (shapeKey: string) => {
      if (!view) {
        return
      }

      const graphics = shapeKeyToGraphicsRef.current.get(shapeKey) ?? []
      const interactiveGraphic = getInteractiveGraphicFromShapeGraphics(graphics)
      if (interactiveGraphic) {
        view.graphics.remove(interactiveGraphic)
        view.graphics.add(interactiveGraphic)
      }
    },
    [view]
  )

  const resetHoveredPoints = useCallback(() => {
    if (!hoveredShapeKeysRef.current) {
      return
    }

    hoveredShapeKeysRef.current.forEach((shapeKey) => {
      applyShapeKeyHighlightState(shapeKey, false)
    })
    hoveredShapeKeysRef.current.clear()
  }, [applyShapeKeyHighlightState])

  // Configure select/hover/unselect pin graphics
  useEffect(() => {
    if (!view) {
      return
    }
    watch(
      () => view.popup,
      (popup) => {
        if (popup) {
          // When the popup is first launched, update its settings
          popup.dockEnabled = isVerySmallScreen
          popup.dockOptions = {
            position: 'auto',
          }

          watch(
            () => popup.selectedFeature,
            (selectedFeature: Graphic | null | undefined) => {
              // Toggle selected/unselected by shapeKey using cache and ref
              const newKey = getGraphicShapeKey(selectedFeature)
              const prevKey = selectedShapeKeyRef.current

              if (prevKey && (!newKey || prevKey !== newKey)) {
                selectedShapeKeyRef.current = null
                applyShapeKeyHighlightState(prevKey, false)
              }

              if (newKey && shapeKeyToGraphicsRef.current.has(newKey)) {
                selectedShapeKeyRef.current = newKey
                applyShapeKeyHighlightState(newKey, true)
                bringInteractiveGraphicToFront(newKey)
              }
            }
          )
          watch(
            () => popup.visible,
            (isVisible: boolean) => {
              setIsPopupVisible(isVisible)
              // When the popup is closed, update the selected point to appear unselected
              if (!isVisible) {
                view.popup?.clear()
                if (selectedShapeKeyRef.current) {
                  applyShapeKeyHighlightState(selectedShapeKeyRef.current, false)
                  selectedShapeKeyRef.current = null
                }
              }
            }
          )
        }
      }
    )
    void view.when(() => {
      on(
        () => view,
        'click',
        async (event) => {
          const response = await view.hitTest(event)

          const selectedGraphics = getGraphicsFromHitTest(response)
          const selectedPoints: Map<string, Graphic> = new Map()

          for (const graphic of selectedGraphics) {
            const key = getGraphicShapeKey(graphic)
            if (key && !selectedPoints.has(key)) {
              const graphicsForShapeKey = shapeKeyToGraphicsRef.current.get(key) ?? []
              const popupFeature = getPopupFeatureGraphicForShapeKey({
                shapeKey: key,
                clickedGraphic: graphic,
                graphicsForShapeKey,
              })
              if (popupFeature) {
                selectedPoints.set(key, popupFeature)
              }
            }
          }

          if (selectedShapeKeyRef.current && !selectedPoints.has(selectedShapeKeyRef.current)) {
            requestAnimationFrame(() => {
              void view.closePopup()
            })
          }

          if (selectedPoints.size) {
            requestAnimationFrame(() => {
              void view.openPopup({
                features: Array.from(selectedPoints.values()),
              })
            })
          }
        }
      )
      on(
        () => view,
        'pointer-move',
        async (event) => {
          const response = await view.hitTest(event)
          const hoveredGraphics = getGraphicsFromHitTest(response)

          if (view.container) {
            view.container.style.cursor = hoveredGraphics.length ? 'pointer' : 'default'
          }

          // Determine currently hovered shape keys from hit test
          const currentHoveredKeys = new Set<string>(
            filterNil(hoveredGraphics.map((g) => getGraphicShapeKey(g)))
          )

          // Compute differences against previous set
          const prevHovered = hoveredShapeKeysRef.current
          const noLongerHovered: string[] = []
          const newlyHovered: string[] = []

          prevHovered.forEach((k) => {
            if (!currentHoveredKeys.has(k)) {
              noLongerHovered.push(k)
            }
          })
          currentHoveredKeys.forEach((k) => {
            if (!prevHovered.has(k)) {
              newlyHovered.push(k)
            }
          })

          // Reset highlight for shapes no longer hovered
          noLongerHovered.forEach((shapeKey) => {
            applyShapeKeyHighlightState(shapeKey, false)
          })

          // Highlight newly hovered shapes (pin or polygon fill)
          newlyHovered.forEach((shapeKey) => {
            applyShapeKeyHighlightState(shapeKey, true)
            bringInteractiveGraphicToFront(shapeKey)
          })

          // Update hovered set
          hoveredShapeKeysRef.current = currentHoveredKeys
        }
      )
      on(
        () => view.popup,
        'trigger-action',
        (event) => {
          const id = event.action?.id || ''
          if (id === 'custom-action') {
            const shapeKey = event.action?.attributes?.shapeKey || ''
            const actionKey = event.action?.attributes?.actionKey || ''
            if (!shapeKey || !actionKey) {
              return
            }
            const shape = shapeKeyToShapeRef.current.get(shapeKey)
            const action = shape?.actions?.find((a) => a.key === actionKey)
            void action?.onClick()
            view.popup?.close()
          }
        }
      )
    })
  }, [applyShapeKeyHighlightState, bringInteractiveGraphicToFront, isVerySmallScreen, view])

  // Configure locate widget
  useEffect(() => {
    if (!view || !isUserLocationEnabled) {
      return
    }

    const locate = new Locate({
      view,
    })

    view.ui.add(locate, 'top-left')
  }, [isUserLocationEnabled, view])

  useEffect(() => {
    // Draw graphics
    if (!view || !shapes) {
      return
    }

    const newCache = new Map<string, Graphic[]>()
    const renderedPolygonKeys = new Set<string>()

    for (const shape of shapes) {
      const isSelectable = shape.isSelectable ?? true // a shape is selectable by default
      const popupTemplate: __esri.PopupTemplateProperties | undefined = isSelectable
        ? {
            actions:
              shape.actions?.map((a) => ({
                type: 'button',
                id: 'custom-action',
                title: a.title,
                icon: a.icon,
                attributes: {
                  shapeKey: shape.shapeKey,
                  actionKey: a.key,
                },
              })) ?? [],
            content: shape.description ?? '',
            title: shape.title,
          }
        : undefined

      const createdGraphics: Graphic[] = []

      const shapeSymbol = shape.symbol ?? getPolygonFillSymbol()
      const attributes = isSelectable
        ? {
            key: shape.shapeKey,
          }
        : undefined

      const centroidPoint = getCentroidPoint(shape.shape)

      if (shape.shape.type === 'coordinates') {
        if ((shape.shape.coordinates?.length ?? 0) > 1) {
          const coordKey = shape.shape.coordinates
            .map((c) => `${c.longitude ?? 0},${c.latitude ?? 0}`)
            .join('|')

          const polygon = new Polygon({
            rings: [
              shape.shape.coordinates.map((coordinate) => [
                coordinate.longitude ?? 0,
                coordinate.latitude ?? 0,
              ]),
            ],
          })
          // Duplicate polygons (same coordinates in the same order) are rendered with a
          // transparent symbol so they don't visually stack semi-transparent fills, but
          // they remain interactive — clicking the polygon area still shows all shapes'
          // popups via ArcGIS's built-in pagination.
          const isDuplicate = renderedPolygonKeys.has(coordKey)
          renderedPolygonKeys.add(coordKey)
          const polygonGraphic = new Graphic({
            geometry: polygon,
            symbol: isDuplicate ? getTransparentFillSymbol() : shapeSymbol,
            popupTemplate,
            attributes,
          })
          createdGraphics.push(polygonGraphic)
        }
      } else {
        const geometry = shape.shape.geometry
        const geometryGraphic = new Graphic({
          geometry,
          symbol: shapeSymbol,
          popupTemplate,
          attributes,
        })
        createdGraphics.push(geometryGraphic)
      }

      if (centroidPoint && shape.showPin !== false) {
        const isShapeSelected = shape.isSelected ?? false
        const pointAttributes = isSelectable
          ? {
              key: shape.shapeKey,
              isSelected: isShapeSelected,
            }
          : isShapeSelected
            ? { isSelected: isShapeSelected }
            : undefined
        const pointGraphic = new Graphic({
          geometry: centroidPoint,
          symbol: isShapeSelected ? getSelectedSymbol() : getUnselectedSymbol(),
          popupTemplate,
          attributes: pointAttributes,
        })
        createdGraphics.push(pointGraphic)
      }

      if (createdGraphics.length) {
        newCache.set(shape.shapeKey, createdGraphics)
        shapeKeyToShapeRef.current.set(shape.shapeKey, shape)
      }
    }

    const toAdd = Array.from(newCache.values()).flat()
    const toRemove = Array.from(shapeKeyToGraphicsRef.current.values()).flat()

    // Update graphics
    view.graphics.addMany(toAdd)
    view.graphics.removeMany(toRemove)

    shapeKeyToGraphicsRef.current = newCache

    const extentPoints: [number, number][] = []
    shapes?.forEach((shape) => {
      if (shape.shape.type === 'coordinates') {
        extentPoints.push(
          ...shape.shape.coordinates.map((coordinate): [number, number] => [
            coordinate?.longitude ?? 0,
            coordinate?.latitude ?? 0,
          ])
        )
      } else {
        const extent = shape.shape.geometry.extent
        if (extent) {
          extentPoints.push(
            [extent.xmin, extent.ymin],
            [extent.xmin, extent.ymax],
            [extent.xmax, extent.ymin],
            [extent.xmax, extent.ymax]
          )
        }
      }
    })

    let pointExtent: __esri.Extent | null = null
    if (extentPoints.length) {
      const centroidPoint = new Multipoint({
        points: extentPoints,
      })
      pointExtent = centroidPoint.extent ?? null
    }

    if (pointExtent && (shouldZoomWhenShapesUpdated || !isFirstRenderCompletedRef.current)) {
      // Only zoom out when the first set of shapes are rendered
      requestAnimationFrame(() => {
        void view.goTo(pointExtent.expand(1.1))
      })
      isFirstRenderCompletedRef.current = true
    }

    if (onShapesRendered) {
      void onShapesRendered()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only re-run when shapes change
  }, [shapes, view])

  // Handle map boundaries change
  useEffect(() => {
    if (!view || !onMapBoundariesChange) {
      return
    }

    const extentHandle = watch(
      () => view.extent,
      (extent) => {
        if (!extent || !isFirstRenderCompletedRef.current) {
          return
        }

        let geographicExtent: __esri.Extent | null = extent
        if (extent.spatialReference?.isWebMercator) {
          const projected = webMercatorUtils.webMercatorToGeographic(extent)
          if (projected && !Array.isArray(projected)) {
            geographicExtent = projected as __esri.Extent
          }
        }

        if (!geographicExtent) {
          return
        }

        const { xmin, ymin, xmax, ymax } = geographicExtent

        void onMapBoundariesChange({
          minLongitude: xmin,
          minLatitude: ymin,
          maxLongitude: xmax,
          maxLatitude: ymax,
        })
      },
      { initial: true }
    )

    return () => {
      extentHandle.remove()
    }
  }, [onMapBoundariesChange, view])

  const mapRef = useRef<HTMLArcgisMapElement>(null)
  useEffect(() => {
    const map = mapRef.current
    if (!map) {
      return
    }

    map.addEventListener('arcgisViewReadyChange', handleViewReady)
    map.addEventListener('mouseleave', resetHoveredPoints)
    return () => {
      map.removeEventListener('arcgisViewReadyChange', handleViewReady)
      map.removeEventListener('mouseleave', resetHoveredPoints)
    }
  }, [handleViewReady, mapRef, resetHoveredPoints])

  const height = props.height ?? `${MapHeightPx}px`

  const layerSegmentedControlPosition = isVerySmallScreen ? 'left-2 bottom-12' : 'right-2 bottom-6'
  const showSegmentedControls = !isVerySmallScreen || !isPopupVisible

  return (
    <div className="relative w-full">
      <arcgis-map
        ref={mapRef}
        basemap={basemap}
        style={{
          height,
          minHeight: height,
        }}
        // @ts-expect-error - class is not a valid prop for arcgis-map, but className does not work
        class={
          // Need String.raw to get the underscore escaping to work: https://github.com/tailwindlabs/tailwindcss/issues/8881
          String.raw`w-full overflow-clip rounded-lg border border-neutral-200 outline-0 [&_.esri-attribution]:!left-auto [&_.esri-attribution]:w-fit [&_.esri-attribution]:rounded-tl-sm [&_.esri-attribution\\_\\_sources]:hidden [&_.esri-view-surface::after]:invisible [&_.esri-view-surface::after]:rounded-lg [&_.esri-view-surface:focus-visible::after]:visible`
        }
        onarcgisViewReadyChange={handleViewReady}
        tabIndex={-1}
        zoom={zoom}
      >
        <arcgis-zoom position="top-right"></arcgis-zoom>
      </arcgis-map>
      {showSegmentedControls && (
        <>
          <div className="absolute bottom-2 left-2">
            <SegmentedControl
              ariaLabel="Select basemap"
              onValueChange={setBasemap}
              value={basemap}
              width="fit-content"
            >
              <SegmentedControl.Item value={Basemap.Map} icon="FaMap">
                Map
              </SegmentedControl.Item>
              <SegmentedControl.Item value={Basemap.Satellite} icon="FaSatellite">
                Satellite
              </SegmentedControl.Item>
            </SegmentedControl>
          </div>
          <div className={twMerge('absolute', layerSegmentedControlPosition)}>
            {props.selectLayerProps ? (
              <SegmentedControl
                ariaLabel="Select layer"
                onValueChange={props.selectLayerProps.onSelectedLayerOptionChange}
                value={props.selectLayerProps.selectedLayerOption}
                width="fit-content"
              >
                {props.selectLayerProps.layerOptions.map((opt) => (
                  <SegmentedControl.Item key={opt.value} value={opt.value} icon={opt.icon}>
                    {opt.label}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export default memo(ArcGisMap)
